import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { ConfirmModal } from '@/components/ui/Modal'
import { Spinner } from '@/components/ui/Spinner'
import { Table } from '@/components/ui/Table'
import { useUrlFilters } from '@/hooks/useUrlFilters'
import { toastService } from '@/lib/toastService'
import { Box, Heading, HStack, IconButton, Stack } from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi'
import { streetTypesApi } from '../api/streetsApi'
import { StreetTypeModal } from '../components/StreetTypeModal'

const columnHelper = createColumnHelper()

export default function StreetTypes() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const { filters, updateFilters, handlePaginationChange, handleSortingChange } = useUrlFilters({
    search: '',
    page: 1,
    page_size: 20,
    ordering: 'short_name',
  })

  // Fetch street types
  const { data, isLoading } = useQuery({
    queryKey: ['street-types', filters.search, filters.page, filters.page_size, filters.ordering],
    queryFn: () => {
      const params = {
        page: filters.page,
        page_size: filters.page_size,
      }
      if (filters.search) params.search = filters.search
      if (filters.ordering) params.ordering = filters.ordering
      return streetTypesApi.getAll(params)
    },
  })

  const totalCount = data?.meta?.pagination?.total_count || data?.meta?.count || 0
  const pageCount = data?.meta?.pagination ? Math.ceil(totalCount / filters.page_size) : 1

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: streetTypesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['street-types'] })
      toastService.success({
        title: t('common.success'),
        description: t('streets.streetTypeDeleted'),
      })
      setDeleteConfirm(null)
    },
    onError: () => {
      toastService.error({
        title: t('common.error'),
        description: t('streets.deleteError'),
      })
    },
  })

  const columns = [
    columnHelper.accessor('id', {
      header: 'ID',
      cell: (info) => info.getValue(),
      meta: { width: '80px' },
    }),
    columnHelper.accessor('type_street', {
      header: t('streets.typeStreet'),
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('short_name', {
      header: t('streets.shortName'),
      cell: (info) => info.getValue(),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('common.actions'),
      cell: (info) => {
        const item = info.row.original
        return (
          <HStack spacing={1}>
            <IconButton
              size="sm"
              variant="ghost"
              aria-label="Edit"
              onClick={() => {
                setEditingItem(item)
                setIsModalOpen(true)
              }}
            >
              <FiEdit2 />
            </IconButton>
            <IconButton
              size="sm"
              variant="ghost"
              colorScheme="red"
              aria-label="Delete"
              onClick={() => setDeleteConfirm(item)}
            >
              <FiTrash2 />
            </IconButton>
          </HStack>
        )
      },
      meta: { width: '120px' },
    }),
  ]

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" />
      </Box>
    )
  }

  return (
    <Stack gap={4}>
      <Heading size="lg">{t('streets.tabs.streetTypes')}</Heading>

      <HStack justify="space-between">
        <Box flex="1" maxW="400px">
          <Input
            placeholder={t('streets.searchPlaceholder')}
            value={filters.search}
            onChange={(e) => updateFilters({ search: e.target.value })}
          />
        </Box>
        <Button onClick={() => { setEditingItem(null); setIsModalOpen(true) }} leftIcon={<FiPlus />}>
          {t('streets.createStreetType')}
        </Button>
      </HStack>

      <Box bg="white" borderRadius="md" border="1px solid" borderColor="#DFE1E6">
        <Table
          data={data?.data || []}
          columns={columns}
          enablePagination
          enableSorting
          manualPagination={!!data?.meta?.pagination}
          manualSorting
          pageCount={pageCount}
          totalCount={totalCount}
          state={{
            pagination: {
              pageIndex: filters.page - 1,
              pageSize: filters.page_size,
            },
            sorting: filters.ordering ? [{
              id: filters.ordering.startsWith('-') ? filters.ordering.slice(1) : filters.ordering,
              desc: filters.ordering.startsWith('-')
            }] : [],
          }}
          onPaginationChange={handlePaginationChange}
          onSortingChange={handleSortingChange}
        />
      </Box>

      <StreetTypeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        streetType={editingItem}
      />

      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => deleteConfirm && deleteMutation.mutate(deleteConfirm.id)}
        title={t('streets.deleteConfirmTitle')}
        message={t('streets.deleteConfirmMessage', { name: deleteConfirm?.type_street })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
      />
    </Stack>
  )
}
